import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useApi } from "@growthos/ui-app-sdk";
import Tabs, { TabPanel } from "@acadia-ui/tabs";
import DataTable, { ColumnTemplate } from "@acadia-ui/datatable";
import EmptyState from "@acadia-ui/empty-state";
import Heading, { HEADING_LEVEL, HEADING_SIZE } from "@acadia-ui/heading";
import { ToastContainer, ToastNotification, showToast } from "@acadia-ui/toast";
import { NavLink } from "@acadia-ui/navigation";

import { loadTaxonomyData, loadCampaignsData } from "./activityLogApiActions";
import { TABS, CAMPAIGN_COLUMNS, TAXONOMY_COLUMNS } from "./constants";
import CustomDataProvider from "./CustomDataProvider";

import "./activityLog.scss";

const showError = (type) => showToast("activityLogErrors", <ToastNotification message={`Error loading ${type} activity data`} type="error" />);

/**
 * Custom hook to manage the activity log view.
 *
 * @returns {Object} - An object containing the rendered element and sub-navigation links.
 * @returns {JSX.Element} return.element - The rendered activity log component.
 * @returns {JSX.Element[]} return.subNavLinks - The sub-navigation links for switching between tabs.
 */
const useActivityLog = () => {
	const { pathname } = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedTab, setSelectedTab] = useState();

	useEffect(() => {
		setSelectedTab(parseInt(searchParams.get("tab") || 0) || 0);
	}, []);

	const {
		isLoading: campaignsLoading,
		data: campaignsData,
		dispatchApiCall: dispatchCampaignsApiCall,
		abortController: campaignsAbortController
	} = useApi(loadCampaignsData, [], { onError: () => showError("campaign"), fetchOnMount: false, cache: true });
	const {
		isLoading: taxonomyLoading,
		data: taxonomyData,
		dispatchApiCall: dispatchTaxonomyApiCall,
		abortController: taxonomyAbortController
	} = useApi(loadTaxonomyData, [], { onError: () => showError("taxonomy"), fetchOnMount: false, cache: true });

	useEffect(() => {
		if (pathname?.toLowerCase?.() === "/activitylog") {
			if (selectedTab == 0 || selectedTab == 1) {
				setSearchParams({ tab: selectedTab });
			}

			if (selectedTab === 0) {
				taxonomyAbortController?.abort?.();
				dispatchCampaignsApiCall?.();
			} else if (selectedTab === 1) {
				campaignsAbortController?.abort?.();
				dispatchTaxonomyApiCall?.();
			}
		} else {
			taxonomyAbortController?.abort?.();
			campaignsAbortController?.abort?.();
		}
	}, [pathname, selectedTab]);

	const campaignsDataProvider = useMemo(() => new CustomDataProvider({ data: campaignsData || [] }), [campaignsData]);
	const taxonomyDataProvider = useMemo(() => new CustomDataProvider({ data: taxonomyData || [] }), [taxonomyData]);

	const emptyState = (
		<EmptyState
			heading={
				<Heading size={HEADING_SIZE.MEDIUM} as={HEADING_LEVEL.H2} description={"There is no data to display."}>
					No Data
				</Heading>
			}
			icon={"icon-info"}
		/>
	);

	const element = useMemo(() => {
		return (
			<div className={`gos-pm-activity-log`}>
				<ToastContainer id="activityLogErrors" />
				<Tabs tabs={TABS} selectedTab={selectedTab}>
					<TabPanel id="campaigns-activity-table">
						<DataTable
							// this comment exists to prevent eslint from making this unreadable.
							title={"Campaigns Activity"}
							bindKey="id"
							data={campaignsDataProvider}
							actionBarProps={{ showEditMode: false, search: true }}
							isLoading={campaignsLoading}
							emptyState={emptyState}
						>
							{CAMPAIGN_COLUMNS.map(({ label, ...columnTemplateProps }) => (
								<ColumnTemplate key={columnTemplateProps.bind} {...columnTemplateProps}>
									{label}
								</ColumnTemplate>
							))}
						</DataTable>
					</TabPanel>
					<TabPanel id="taxonomy-activity-table">
						<DataTable
							// this comment exists to prevent eslint from making this unreadable.
							title={"Taxonomy Activity"}
							bindKey="id"
							data={taxonomyDataProvider}
							actionBarProps={{ showEditMode: false, search: true }}
							isLoading={taxonomyLoading}
							emptyState={emptyState}
						>
							{TAXONOMY_COLUMNS.map(({ label, ...columnTemplateProps }) => (
								<ColumnTemplate key={columnTemplateProps.bind} {...columnTemplateProps}>
									{label}
								</ColumnTemplate>
							))}
						</DataTable>
					</TabPanel>
				</Tabs>
			</div>
		);
	}, [selectedTab, taxonomyLoading, campaignsLoading, campaignsData, taxonomyData]);

	const subNavLinks = useMemo(
		() => [
			<NavLink
				// this comment exists to prevent eslint from making this unreadable.
				key="campaigns-activity-table"
				selected={selectedTab == 0}
				onClick={() => setSelectedTab(0)}
			>
				Campaigns Activity
			</NavLink>,
			<NavLink
				// this comment exists to prevent eslint from making this unreadable.
				key="taxonomy-activity-table"
				selected={selectedTab == 1}
				onClick={() => setSelectedTab(1)}
			>
				Taxonomy Activity
			</NavLink>
		],
		[selectedTab]
	);

	return useMemo(() => ({ element, subNavLinks }), [element, subNavLinks]);
};

export default useActivityLog;
